// HTML → markdown serializer. Walks the contenteditable's DOM and
// reduces it to the same mrkdwn-ish line format the wire envelope
// expects.
//
// Inverse of `markdownToHtml`. The two together provide a stable
// round-trip for the format set the editor exposes (bold, italic,
// strike, inline-code, quote, bullet/numbered list (nested), code-
// block, link). Anything outside that set is collapsed to text.

import { detectLanguage } from './detectLanguage';

const INLINE_TAG_MARK: Readonly<Record<string, string>> = {
  STRONG: '*',
  B: '*',
  EM: '_',
  I: '_',
  S: '~',
  STRIKE: '~',
  DEL: '~',
};

/**
 * Convert the live DOM of a contenteditable root (or any HTMLElement
 * with that structure) into the markdown wire form.
 */
export function htmlToMarkdown(root: HTMLElement): string {
  const out: string[] = [];
  for (const child of Array.from(root.childNodes)) {
    serializeBlock(child, out);
  }
  // Strip trailing empty lines — browsers commonly leave a stray
  // empty `<p>` after the last block. The editor's empty state is
  // an empty string, not "\n".
  while (out.length > 0 && out[out.length - 1] === '') out.pop();
  return out.join('\n');
}

function serializeBlock(node: Node, out: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').replace(/ /g, ' ');
    if (text.length > 0) out.push(text);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;
  const tag = el.tagName;

  if (tag === 'P' || tag === 'DIV') {
    // Paragraph block. Soft line breaks (`<br>`) within the
    // paragraph emit as additional output lines so the multi-line
    // intent survives the round-trip.
    for (const frag of splitOnBr(el)) {
      const raw = frag.textContent ?? '';
      // Drop a fragment whose ONLY content is the zero-width sentinel
      // (`​`) inserted by the empty-mark toggle — it's never user
      // text, and left in it serializes to a spurious blank line. A
      // genuinely empty line (user pressed Enter) has no sentinel and
      // is preserved.
      if (raw.length > 0 && raw.replace(/[​]/g, '') === '') continue;
      out.push(serializeInline(frag));
    }
    return;
  }

  if (tag === 'BLOCKQUOTE') {
    const lines = splitOnBr(el).map(serializeInline);
    for (const line of lines) out.push(`> ${line}`);
    return;
  }

  if (tag === 'UL' || tag === 'OL') {
    serializeList(el, 0, out);
    return;
  }

  if (tag === 'PRE') {
    // Pre wraps a single <code> child holding the verbatim code-block payload.
    // Read it `<br>`-aware: a newline the user typed inside the block lands as
    // a `<br>` element (contenteditable default), which `textContent` would
    // silently drop — collapsing a multi-line snippet onto one line. Convert
    // each `<br>` to `\n` so every newline survives to the wire fence.
    // Read the body from the `<code>` ONLY — never fall back to the `<pre>`
    // itself. The `<pre>` also holds the always-present language chip
    // (`ping-code-lang-chip`, a sibling of `<code>`); reading the `<pre>`
    // (as a `?? el` fallback did) would splice the chip's label text ("Plain
    // Text", …) into the message body when a delete-all leaves a malformed,
    // `<code>`-less `<pre>`. No `<code>` ⇒ empty block.
    const codeEl = el.querySelector('code');
    // A `<code>` whose only child is a `<br>` is the empty-block caret
    // placeholder, not content — serialize it as an empty fence, not a blank
    // line.
    const placeholderOnly =
      codeEl === null ||
      (codeEl.childNodes.length === 1 && codeEl.firstChild?.nodeName === 'BR');
    // Drop a single trailing newline: it's either the leftover `<br>` caret
    // placeholder that the first typed char inserted in FRONT of, or a trailing
    // blank line — and a fenced body never includes the newline that precedes
    // its closing fence (CommonMark). Interior newlines are untouched.
    const body =
      codeEl === null || placeholderOnly
        ? ''
        : codeBlockText(codeEl).replace(/ /g, ' ').replace(/\n$/, '');
    // Language label: an explicit user/parser-set `data-lang` wins;
    // otherwise auto-detect from the body so the fence carries an
    // accurate language even when the user never picked one.
    const explicit = (el.getAttribute('data-lang') ?? '').trim().toLowerCase();
    const lang =
      explicit.length > 0 ? explicit : detectLanguage(body) ?? '';
    // CommonMark variable-length fence. A code block whose body itself
    // contains a ``` run (a NESTED code block) can't be represented with a
    // fixed three-backtick fence — the receiver's tokenizer would close the
    // outer block at the first inner ```, mangling everything after it. Size
    // the fence LONGER than the longest backtick run in the body so no inner
    // line can be mistaken for the closer.
    const longestTickRun = (body.match(/`+/g) ?? []).reduce(
      (max, run) => Math.max(max, run.length),
      0,
    );
    // Honor an authored opening-fence length (`data-fence`) as a floor so a
    // block the user opened with 4+ backticks round-trips at that length even
    // before its nested content is typed.
    const authoredFence =
      Number.parseInt(el.getAttribute('data-fence') ?? '0', 10) || 0;
    const fence = '`'.repeat(
      Math.max(3, longestTickRun + 1, authoredFence),
    );
    out.push(lang.length > 0 ? `${fence}${lang}` : fence);
    if (body.length > 0) {
      for (const line of body.split('\n')) out.push(line);
    }
    out.push(fence);
    return;
  }

  if (tag === 'BR') {
    out.push('');
    return;
  }

  // Unknown block-level element — fall back to inline serialization
  // so we don't drop content. The toolbar can't have produced this
  // but a paste might.
  const fallback = serializeInline(el);
  if (fallback.length > 0) out.push(fallback);
}

/**
 * Verbatim text of a code block's `<code>` (or `<pre>`), with each `<br>`
 * rendered as `\n`. Unlike `textContent` this preserves newlines the user
 * typed inside the block, which the browser materializes as `<br>` elements.
 */
function codeBlockText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR') {
    return '\n';
  }
  let out = '';
  for (const child of Array.from(node.childNodes)) out += codeBlockText(child);
  return out;
}

/**
 * Serialize a `<ul>` or `<ol>` (possibly with nested lists in its
 * `<li>` children) into markdown lines with 2-space indentation per
 * nesting level. Numbered lists renumber each time so deleting an
 * item in the middle never produces a `1. 2. 4.` sequence.
 */
function serializeList(list: HTMLElement, depth: number, out: string[]): void {
  const tag = list.tagName;
  const items = Array.from(list.children).filter(
    (n) => n.tagName === 'LI',
  ) as HTMLElement[];
  const indent = '  '.repeat(depth);
  items.forEach((li, idx) => {
    // Separate the item's own text from any nested lists so we can
    // emit the prefix line first, then recurse.
    const ownNodes: Node[] = [];
    const nested: HTMLElement[] = [];
    for (const child of Array.from(li.childNodes)) {
      if (
        child.nodeType === Node.ELEMENT_NODE &&
        ((child as HTMLElement).tagName === 'UL' ||
          (child as HTMLElement).tagName === 'OL')
      ) {
        nested.push(child as HTMLElement);
      } else {
        ownNodes.push(child);
      }
    }
    const doc = li.ownerDocument;
    const shadow = doc.createElement('span');
    for (const n of ownNodes) shadow.appendChild(n.cloneNode(true));
    // A list item is a SINGLE markdown line: a placeholder `<br>` in an
    // empty item (or a soft break) must not emit a real newline, or it
    // would inject a phantom blank line after the marker (e.g. toggling
    // a bullet on the empty composer producing "- \n"). Collapse any
    // newline the inline serializer produced from `<br>`s to a space and
    // trim the trailing run.
    const text = serializeInline(shadow).replace(/\n+/g, ' ').replace(/ +$/, '');
    const prefix = tag === 'UL' ? '- ' : `${idx + 1}. `;
    out.push(`${indent}${prefix}${text}`);
    for (const nestedList of nested) {
      serializeList(nestedList, depth + 1, out);
    }
  });
}

/**
 * Split a block's children on `<br>` boundaries. Returns one fragment
 * per visual line; each fragment is a thin wrapper element so the
 * inline serializer can walk its children uniformly.
 */
function splitOnBr(el: HTMLElement): HTMLElement[] {
  const groups: HTMLElement[] = [];
  const doc = el.ownerDocument;
  let current = doc.createElement('span');
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'BR') {
      groups.push(current);
      current = doc.createElement('span');
      continue;
    }
    current.appendChild(child.cloneNode(true));
  }
  groups.push(current);
  // Drop a trailing empty group produced by a "bogus" trailing `<br>`
  // — contenteditable appends one to make an empty last line visible
  // (`<p>text<br></p>`, `<p><br></p>`). Counting it would emit a
  // phantom extra blank line on every paragraph.
  if (groups.length > 1) {
    const last = groups[groups.length - 1];
    if (last !== undefined && (last.textContent ?? '') === '') groups.pop();
  }
  return groups;
}

function serializeInline(el: HTMLElement): string {
  let out = '';
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      // Strip zero-width spaces — the inline-mark command inserts
      // them inside empty marks so the caret has somewhere to sit,
      // but they shouldn't survive serialization.
      out += (child.textContent ?? '').replace(/ /g, ' ').replace(/​/g, '');
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const c = child as HTMLElement;
    const tag = c.tagName;
    if (tag === 'BR') {
      // Inside an inline context a BR is a no-op (split happens at
      // block serializer); preserve as a space so adjacent words
      // don't fuse.
      out += '\n';
      continue;
    }
    if (tag === 'CODE') {
      // Inline code — wrap with backticks; do NOT recurse into
      // child marks (mrkdwn doesn't nest into inline code).
      out += `\`${c.textContent ?? ''}\``;
      continue;
    }
    if (tag === 'A') {
      const href = c.getAttribute('href') ?? '';
      const label = serializeInline(c);
      out += `[${label}](${href})`;
      continue;
    }
    if (tag === 'SPAN' && c.hasAttribute('data-mention-id')) {
      // Mention chip — serialize back to `@displayName` so the wire
      // format stays compatible with non-rich clients. The host can
      // resolve to a user-id via its mention parser if needed.
      const display = c.textContent ?? '';
      out += display;
      continue;
    }
    const mark = INLINE_TAG_MARK[tag];
    if (mark !== undefined) {
      const inner = serializeInline(c);
      if (inner.length === 0) continue;
      out += `${mark}${inner}${mark}`;
      continue;
    }
    // Unknown inline element — recurse into its content so text is
    // preserved.
    out += serializeInline(c);
  }
  return out;
}
